import React, { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Checkbox, Text, VStack, useToast, Center, Spinner } from '@chakra-ui/react'
import DoppelModal from './doppel_design/doppel_modal'
import { DOPPEL_TEXT_WHITE, FONT_SIZE_MEDIUM } from '@/utils/style'
import { OnboardingStatus } from '@/generated/enums'
import { useUserID } from '@/hooks/id_token_claims'
import { TOS_URL, PRIVACY_URL } from '@/utils/constants'

import {
  useGetUserOnboardingStatusQuery,
  useActivateUserMutation,
} from '@/generated/graphql'

type ToSWrapperProps = {
  children: React.ReactNode
}

const ToSWrapper: React.FC<ToSWrapperProps> = ({ children }) => {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth0()
  const toast = useToast()
  const [activateUserMutation] = useActivateUserMutation()

  const [userId] = useUserID()
  const [hasAgreed, setHasAgreed] = useState(false)
  const [isPending, setIsPending] = useState(false)

  // Only run query when userId is ready
  const { data: userData, loading: userLoading } = useGetUserOnboardingStatusQuery({
    variables: { userId },
    skip: !userId,
  })

  const activateUser = async () => {
    await activateUserMutation({
      variables: { user_id: userId },
      onCompleted: () => {
        toast({
          title: 'Terms of Service accepted',
          description: 'Thank you for accepting our Terms of Service.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
        setIsPending(false)
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: `Failed to update Terms of Service acceptance: ${error.message}`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    })
  }

  useEffect(() => {
    // Show TOS if user exists but has no onboarding status, or if status is PENDING
    if (userData?.users_by_pk) {
      const status = userData.users_by_pk.onboarding_status?.toLowerCase()
      const isPendingStatus = status === OnboardingStatus.PENDING.toLowerCase()
      const hasNoStatus = !status

      setIsPending(isPendingStatus || hasNoStatus)
    }
  }, [userData])

  // 1. Auth is still initializing
  if (authLoading) {
    return (
      <Center height="100vh">
        <Spinner color="blue.500" size="xl" thickness="4px" />
      </Center>
    )
  }

  // 2. User is logged out — don't show any TOS logic, just show the app
  if (!isAuthenticated) {
    return <>{children}</>
  }

  // 3. User is logged in, but still loading userId or onboarding status
  if (!userId || userLoading) {
    return (
      <Center height="100vh">
        <Spinner color="blue.500" size="xl" thickness="4px" />
      </Center>
    )
  }

  const modalBody = (
    <VStack align="start" spacing={4}>
      <Text fontSize={FONT_SIZE_MEDIUM}>
        By clicking <b>Accept</b>, you are confirming that you understand and agree to
        the{' '}
        <a
          href={TOS_URL}
          rel="noreferrer"
          style={{ textDecoration: 'underline' }}
          target="_blank"
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href={PRIVACY_URL}
          rel="noreferrer"
          style={{ textDecoration: 'underline' }}
          target="_blank"
        >
          Privacy Policy
        </a>
        .
      </Text>

      <Checkbox
        color={DOPPEL_TEXT_WHITE}
        isChecked={hasAgreed}
        onChange={(e) => setHasAgreed(e.target.checked)}
      >
        <Text fontSize={FONT_SIZE_MEDIUM}>Confirm and proceed</Text>
      </Checkbox>
    </VStack>
  )

  return (
    <>
      {isPending && (
        <DoppelModal
          body={modalBody}
          isAsync={false}
          isDisabled={!hasAgreed}
          isOpen={isPending}
          maxWidth="50vw"
          onClose={() => {}}
          primaryAction={activateUser}
          primaryLabel="Accept"
          showCancelButton={false}
          title="Terms of Service"
        />
      )}

      {!isPending && children}
    </>
  )
}

export default ToSWrapper
